import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
  Avatar,
  AvatarFallback,
  ScrollArea,
  Separator,
  useToast,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui";
import {
  Loader2,
  Send,
  Pencil,
  Trash2,
  X,
  Check,
  MessageSquarePlus,
} from "lucide-react";
import { formatRelativeTime } from "@repo/utils";
import { useAuth } from "@/lib/auth/auth-provider";
import { getNotes, createNote, updateNote, deleteNote } from "@/lib/api/notes";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import type { Note } from "@/lib/types/inventory";

interface NotesThreadProps {
  open: boolean;
  onClose: () => void;
  entityType: "inventory" | "receipt" | "dispense" | "adjustment" | "reminder";
  entityId: string;
  ndc: string;
  itemName: string;
  onNotesCountChange?: (count: number) => void;
}

export function NotesThread({
  open,
  onClose,
  entityType,
  entityId,
  ndc,
  itemName,
  onNotesCountChange,
}: NotesThreadProps) {
  const [notes, setNotes] = React.useState<Note[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [newNote, setNewNote] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);
  const [editContent, setEditContent] = React.useState("");
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [deleteNoteId, setDeleteNoteId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const auth = useAuth();
  const activeUserId = auth.user?.id ?? null;
  const userRole = auth.user?.role ?? null;
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const fetchNotes = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getNotes({ entityType, entityId });
      const notesList = (data as { data?: Note[] }).data || [];
      setNotes(notesList);
      onNotesCountChange?.(notesList.length);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to load notes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId, onNotesCountChange, toast]);

  // Fetch notes when modal opens
  React.useEffect(() => {
    if (open && entityId) {
      fetchNotes();
    }
  }, [open, entityId, entityType, fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      const data = await createNote({
        entityType,
        entityId,
        content: newNote.trim(),
      });

      // Add the new note to the top of the list
      const newNoteData = (data as { data: Note }).data;
      setNotes((prev) => [newNoteData, ...prev]);
      onNotesCountChange?.(notes.length + 1);
      setNewNote("");

      toast({
        title: "Note Added",
        description: "Your note has been added successfully.",
        variant: "success",
      });
    } catch (error) {
      // Capture content before approval flow
      const capturedContent = newNote.trim();
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {
            const retryData = await createNote({
              entityType,
              entityId,
              content: capturedContent,
            });
            const retryNote = (retryData as { data: Note }).data;
            setNotes((prev) => [retryNote, ...prev]);
            onNotesCountChange?.(notes.length + 1);
            setNewNote("");
            toast({
              title: "Note Added",
              description: "Your note has been added successfully.",
              variant: "success",
            });
          },
          actionDescription: "Add note",
        })
      )
        return;
      console.error("Error adding note:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to add note",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (note: Note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editContent.trim()) return;

    setIsUpdating(true);
    try {
      const data = await updateNote(editingNoteId, {
        content: editContent.trim(),
      });

      // Update note in list
      const updatedNote = (data as { data: Note }).data;
      setNotes((prev) =>
        prev.map((n) => (n.id === editingNoteId ? updatedNote : n))
      );
      setEditingNoteId(null);
      setEditContent("");

      toast({
        title: "Note Updated",
        description: "Your note has been updated successfully.",
        variant: "success",
      });
    } catch (error) {
      // Capture values before approval flow
      const capturedNoteId = editingNoteId;
      const capturedContent = editContent.trim();
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {
            const retryData = await updateNote(capturedNoteId, {
              content: capturedContent,
            });
            const retryNote = (retryData as { data: Note }).data;
            setNotes((prev) =>
              prev.map((n) => (n.id === capturedNoteId ? retryNote : n))
            );
            setEditingNoteId(null);
            setEditContent("");
            toast({
              title: "Note Updated",
              description: "Your note has been updated successfully.",
              variant: "success",
            });
          },
          actionDescription: "Update note",
        })
      )
        return;
      console.error("Error updating note:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to update note",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteNoteId) return;

    setIsDeleting(true);
    try {
      await deleteNote(deleteNoteId);

      // Remove from list
      setNotes((prev) => prev.filter((n) => n.id !== deleteNoteId));
      onNotesCountChange?.(notes.length - 1);
      setDeleteNoteId(null);

      toast({
        title: "Note Deleted",
        description: "The note has been deleted.",
        variant: "success",
      });
    } catch (error) {
      const capturedDeleteId = deleteNoteId;
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {
            await deleteNote(capturedDeleteId);
            setNotes((prev) => prev.filter((n) => n.id !== capturedDeleteId));
            onNotesCountChange?.(notes.length - 1);
            setDeleteNoteId(null);
            toast({
              title: "Note Deleted",
              description: "The note has been deleted.",
              variant: "success",
            });
          },
          actionDescription: "Delete note",
        })
      )
        return;
      console.error("Error deleting note:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to delete note",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const canModifyNote = (note: Note) => {
    if (!activeUserId) return false;
    // Author can always modify their own notes
    if (note.created_by === activeUserId) return true;
    // Supervisors and admins can modify any note
    if (userRole && ["admin", "supervising_pharmacist"].includes(userRole)) {
      return true;
    }
    return false;
  };

  const entityLabel =
    entityType === "inventory"
      ? "Inventory"
      : entityType === "receipt"
        ? "Receipt"
        : entityType === "adjustment"
          ? "Adjustment"
          : "Dispense";

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] flex flex-col max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{entityLabel} Notes</DialogTitle>
            <DialogDescription>
              View and add notes for this {entityType}. All notes are visible to
              pharmacy staff.
            </DialogDescription>
          </DialogHeader>

          {/* Item Info */}
          <div className="rounded-lg bg-muted p-3 space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-muted-foreground">NDC:</span>
              <span className="font-mono">{ndc}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-muted-foreground">Item:</span>
              <span>{itemName}</span>
            </div>
          </div>

          {/* Add Note Input */}
          <div className="space-y-2">
            <Textarea
              placeholder="Write a note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={2}
              className="resize-none"
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleAddNote();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {newNote.length}/1000 characters
              </p>
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={isSubmitting || !newNote.trim()}
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Add Note
              </Button>
            </div>
          </div>

          <Separator />

          {/* Notes List */}
          <ScrollArea className="flex-1 min-h-0 max-h-[40vh]" ref={scrollRef}>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <MessageSquarePlus className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">No notes yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Be the first to add a note.
                </p>
              </div>
            ) : (
              <div className="space-y-4 pr-4">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="group rounded-lg border p-3 space-y-2 transition-colors hover:bg-muted/30"
                  >
                    {/* Note Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-xs bg-primary/10 text-primary">
                            {note.created_by_user?.initials || "??"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium leading-none">
                            {note.created_by_user?.full_name || "Unknown User"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatRelativeTime(note.created_at)}
                            {note.updated_at !== note.created_at && (
                              <span className="ml-1 italic">(edited)</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {canModifyNote(note) && editingNoteId !== note.id && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleStartEdit(note)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => setDeleteNoteId(note.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Note Content */}
                    {editingNoteId === note.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={3}
                          className="resize-none text-sm"
                          disabled={isUpdating}
                          autoFocus
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                          >
                            <X className="mr-1 h-3.5 w-3.5" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={isUpdating || !editContent.trim()}
                          >
                            {isUpdating ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="mr-1 h-3.5 w-3.5" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {note.content}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteNoteId}
        onOpenChange={(open) => !open && setDeleteNoteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
